import useCallingDisabled from '@lib/hooks/useCallingDisabled';
import { Tooltip } from '@servicetitan/design-system';
import { cloneElement, ReactElement, useRef } from 'react';
import { useHover } from 'usehooks-ts';

interface CallingDisabledGuardProps {
  children: JSX.Element;
  additionalDisabled?: boolean;
}

interface CallingDisabledChildProps {
  disabled: boolean;
  onClick: () => void;
}

/**
 * `CallingDisabledGuard` is a component that wraps its children and conditionally disables them
 * based on the `callingDisabled` state and an additional disabled condition.
 * It also displays a tooltip with a reason when the child element is hovered over and calling is disabled.
 *
 * @param {React.ReactNode} children - The child element to be wrapped and conditionally disabled.
 * @param {boolean} additionalDisabled - An additional condition to determine if the child element should be disabled.
 *
 * @returns {JSX.Element} A Tooltip component wrapping the potentially disabled child element.
 */
const CallingDisabledGuard = ({
  children: child,
  additionalDisabled,
}: CallingDisabledGuardProps) => {
  const { callingDisabled, reason } = useCallingDisabled();
  const hoverRef = useRef(null);
  const isHover = useHover(hoverRef);
  const disabled = callingDisabled || additionalDisabled;

  if (!disabled) {
    return child;
  }

  let childElement: ReactElement<CallingDisabledChildProps> | null = null;

  childElement = cloneElement(child, {
    disabled: callingDisabled || additionalDisabled,
    onClick: () => {},
  });

  return (
    <div ref={hoverRef}>
      <Tooltip
        data-testid={'calling-disabled-tooltip'}
        open={isHover && callingDisabled}
        text={reason}
      >
        {childElement}
      </Tooltip>
    </div>
  );
};

export default CallingDisabledGuard;
